<template>
  <div class="full-page" fill-height :style="{ backgroundImage: `url(${require('@/assets/Culture.png')})` }">
    <v-container fill-height fluid class="d-flex align-center justify-center">
      <v-row >
        <v-col cols="12" md="4">
          <v-card class="login-card" transition="scale-transition">
            <div class="card-header">
              <img src="@/assets/nlogo.png" class="logo-image fade-in" alt="Logo" />
              <div class="login-text montserrat-title fade-in">LOGIN</div>
            </div>
            <v-card-text>
              <div class="text-center">
                <v-alert
                  v-if="alert.visible"
                  :type="alert.type"
                  dismissible
                  class="mb-4 montserrat-alert fade-in"
                  @input="alert.visible = false"
                >
                  {{ alert.message }}
                </v-alert>
                <v-row class="my-4">
                  <v-text-field
                    v-model="email"
                    solo
                    rounded
                    class="montserrat-input fade-in"
                    hide-details
                    prepend-inner-icon="mdi-email"
                  ></v-text-field>
                </v-row>
                <v-row class="my-4">
                  <v-text-field
                    v-model="password"
                    :type="passwordVisible ? 'text' : 'password'"
                    solo
                    rounded
                    class="montserrat-input fade-in"
                    hide-details
                    prepend-inner-icon="mdi-lock"
                    append-icon="mdi-eye"
                    @click:append="togglePasswordVisibility"
                  ></v-text-field>
                </v-row>

                <div class="additional-links mb-7 fade-in">
                  <v-btn text class="montserrat-button" @click="showForgotPasswordDialog">
                    <v-icon left>mdi-lock-reset</v-icon>Forget Password?
                  </v-btn>
                </div>
                <v-btn 
                  dark 
                  rounded 
                  large 
                  width="100%" 
                  color="#991d23" 
                  @click="submit" 
                  class="montserrat-button fade-in"
                  :loading="loggingIn"
                  :disabled="loggingIn"
                >
                  <v-icon left>mdi-login</v-icon>
                  {{ loggingIn ? 'SIGNING IN...' : 'LOGIN' }}
                </v-btn>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Single Device Warning Dialog -->
    <v-dialog v-model="singleDeviceDialog" :retain-focus="false" persistent max-width="500px">
      <v-card>
        <v-card-title class="headline montserrat-title">
          <v-icon left color="warning">mdi-alert-circle</v-icon>
          Account Already Active
        </v-card-title>
        <v-card-text class="montserrat-input">
          <p>This account is already logged in on another device:</p>
          <v-card outlined class="mt-3 pa-3">
            <div><strong>Device:</strong> {{ conflictingSession.deviceType }}</div>
            <div><strong>Browser:</strong> {{ conflictingSession.browser }}</div>
            <div><strong>OS:</strong> {{ conflictingSession.os }}</div>
            <div><strong>Login Time:</strong> {{ conflictingSession.loginTime }}</div>
          </v-card>
          <p class="mt-3">Only one device can be logged in at a time for security reasons.</p>
          <p><strong>Would you like to:</strong></p>
        </v-card-text>
        <v-card-actions>
          <v-btn 
            color="grey" 
            text 
            @click="cancelLogin" 
            class="montserrat-button"
          >
            <v-icon left>mdi-cancel</v-icon>Cancel
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn 
            color="#991d23" 
            dark 
            @click="forceLogin" 
            class="montserrat-button"
          >
            <v-icon left>mdi-login</v-icon>Login Here (Logout Other Device)
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Forgot Password Dialog -->
    <v-dialog v-model="forgotPasswordDialog" :retain-focus="false" persistent max-width="400px" transition="scale-transition">
      <v-card>
        <v-card-title class="headline montserrat-title fade-in">Forgot Password</v-card-title>
        <v-card-text>
          <v-alert
            v-if="resetAlert.visible"
            :type="resetAlert.type"
            dismissible
            class="mb-4 montserrat-alert fade-in"
            @input="resetAlert.visible = false"
          >
            {{ resetAlert.message }}
          </v-alert>
          <v-text-field
            v-model="forgotPasswordEmail"
            label="Enter your email"
            solo
            rounded
            hide-details
            class="montserrat-input fade-in"
            prepend-inner-icon="mdi-email"
          ></v-text-field>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="blue darken-1" text @click="forgotPasswordDialog = false" class="montserrat-button fade-in">
            <v-icon left>mdi-cancel</v-icon>Cancel
          </v-btn>
          <v-btn color="blue darken-1" text @click="sendPasswordResetRequest" class="montserrat-button fade-in">
            <v-icon left>mdi-send</v-icon>Send
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { getAuth, signInWithEmailAndPassword, signOut } from "firebase/auth";
import { 
  getFirestore, 
  collection, 
  getDocs, 
  query, 
  where, 
  addDoc, 
  updateDoc, 
  doc, 
  serverTimestamp,
  limit
} from "firebase/firestore";
import { firebaseApp } from "@/firebaseConfig";
import { SESSION_CREDENTIALS_KEY } from "@/utils/crossAppLoginUrl";

const auth = getAuth(firebaseApp);
const db = getFirestore(firebaseApp);

export default {
  data() {
    return {
      email: '',
      password: '',
      passwordVisible: false,
      loggingIn: false,
      forgotPasswordDialog: false,
      forgotPasswordEmail: '',
      singleDeviceDialog: false,
      conflictingSession: {},
      pendingLoginCredentials: null,
      cachedIP: null,
      alert: {
        visible: false,
        message: '',
        type: 'success'
      },
      resetAlert: {
        visible: false,
        message: '',
        type: 'success'
      }
    };
  },
  
  mounted() {
    // Pre-fetch IP address on page load
    this.prefetchIPAddress();
  },

  methods: {
    // Pre-fetch IP when page loads
    async prefetchIPAddress() {
      try {
        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 3000);
        
        const response = await fetch('https://api.ipify.org?format=json', {
          signal: controller.signal
        });
        clearTimeout(timeoutId);
        const data = await response.json();
        this.cachedIP = data.ip;
        console.log('IP prefetched:', this.cachedIP);
      } catch (error) {
        console.log('IP prefetch failed (non-critical):', error);
        this.cachedIP = 'Unknown';
      }
    },

    async submit() {
      if (!this.email || !this.password) {
        this.showAlert('Please enter both email and password', 'error');
        return;
      }

      this.loggingIn = true;

      try {
        // Step 1: Authenticate (300ms)
        const userCredential = await signInWithEmailAndPassword(auth, this.email, this.password);
        const user = userCredential.user;

        // Step 2: Parallel execution - user check + session check
        const [userCheck, sessionCheck] = await Promise.all([
          this.checkUserStatus(user.uid),
          this.quickSessionCheck(user.uid)
        ]);
        
        // Validate user status
        if (!userCheck.exists) {
          await signOut(auth);
          this.showAlert('User account not found. Please contact your administrator.', 'error');
          this.loggingIn = false;
          return;
        }

        if (userCheck.disabled) {
          await signOut(auth);
          this.showAlert('Your account has been disabled. Please contact your administrator.', 'error');
          this.loggingIn = false;
          return;
        }

        // Check for session conflict
        if (sessionCheck.conflict) {
          this.pendingLoginCredentials = { user, userCheck };
          this.conflictingSession = sessionCheck.conflictingSession;
          this.singleDeviceDialog = true;
          this.loggingIn = false;
          return;
        }

        // Success - immediate redirect
        localStorage.setItem('role', userCheck.userData.role);
        localStorage.setItem('accessLevel', userCheck.userData.accessLevel || 'Employee');
        localStorage.setItem('department', userCheck.userData.department || '');
        sessionStorage.setItem(
          SESSION_CREDENTIALS_KEY,
          JSON.stringify({
            email: this.email.trim().toLowerCase(),
            password: this.password,
          })
        );
        this.showAlert('Login successful!', 'success');
        
        // Background tasks (non-blocking)
        this.handleSessionInBackground(user.uid, userCheck.userDocId);
        
        setTimeout(() => {
          this.$router.push('/Activity');
        }, 300);

      } catch (error) {
        console.error('Login error:', error);
        this.showAlert('Email or Password is incorrect', 'error');
        this.loggingIn = false;
      }
    },

    // Fast user status check (200ms)
    async checkUserStatus(userUID) {
      const usersCollection = collection(db, 'users');
      const userQuery = query(usersCollection, where('userID', '==', userUID));
      const querySnapshot = await getDocs(userQuery);

      if (querySnapshot.empty) {
        return { exists: false, disabled: false };
      }

      const userDoc = querySnapshot.docs[0];
      const userData = userDoc.data();
      
      return { 
        exists: true, 
        disabled: userData.disabled === true,
        userData: userData,
        userDocId: userDoc.id
      };
    },

    // Fast session check with indexed query (150-300ms)
    async quickSessionCheck(userUID) {
      try {
        const sessionId = this.getOrCreateSessionId();
        
        // Fast indexed query with limit(1)
        const activeSessionQuery = query(
          collection(db, "activeSessions"), 
          where("userUID", "==", userUID),
          where("isActive", "==", true),
          limit(1)
        );
        
        const activeSessions = await getDocs(activeSessionQuery);

        // If no active sessions found
        if (activeSessions.empty) {
          return { conflict: false };
        }

        const sessionDoc = activeSessions.docs[0];
        const sessionData = sessionDoc.data();

        // Check if it's the same device
        if (sessionData.sessionId === sessionId) {
          return { conflict: false };
        }

        // Check if session is stale (older than 2 hours)
        const sessionTime = sessionData.startTime ? 
          new Date(sessionData.startTime.seconds * 1000) : 
          new Date(0);
        const twoHoursAgo = new Date(Date.now() - 2 * 60 * 60 * 1000);

        if (sessionTime < twoHoursAgo) {
          // Stale session, allow login
          return { conflict: false };
        }

        // Active session on different device
        return {
          conflict: true,
          conflictingSession: {
            deviceType: sessionData.deviceInfo?.type || 'Unknown',
            browser: sessionData.deviceInfo?.browser || 'Unknown',
            os: sessionData.deviceInfo?.os || 'Unknown',
            loginTime: sessionData.startTime ? 
              new Date(sessionData.startTime.seconds * 1000).toLocaleString('en-ZA') : 
              'Unknown',
            sessionDocId: sessionDoc.id
          }
        };
      } catch (error) {
        console.error("Error checking session:", error);
        // On error, allow login (fail open for user experience)
        return { conflict: false };
      }
    },

    async forceLogin() {
      if (!this.pendingLoginCredentials) return;

      this.singleDeviceDialog = false;
      this.loggingIn = true;

      try {
        const { user, userCheck } = this.pendingLoginCredentials;
        
        // Store role and redirect immediately
        localStorage.setItem('role', userCheck.userData.role);
        localStorage.setItem('accessLevel', userCheck.userData.accessLevel || 'Employee');
        localStorage.setItem('department', userCheck.userData.department || '');
        sessionStorage.setItem(
          SESSION_CREDENTIALS_KEY,
          JSON.stringify({
            email: this.email.trim().toLowerCase(),
            password: this.password,
          })
        );
        this.showAlert('Login successful!', 'success');
        
        // Handle session cleanup in background
        this.handleSessionInBackground(user.uid, userCheck.userDocId, true);
        
        setTimeout(() => {
          this.$router.push('/Activity');
        }, 300);
        
      } catch (error) {
        console.error('Error during force login:', error);
        this.showAlert('An error occurred. Please try again.', 'error');
        this.loggingIn = false;
      }
    },

    cancelLogin() {
      this.singleDeviceDialog = false;
      this.pendingLoginCredentials = null;
      this.loggingIn = false;
      signOut(auth);
    },

    // Background session management (non-blocking)
    handleSessionInBackground(userUID, userDocId, forceLogout = false) {
      const sessionId = this.getOrCreateSessionId();
      
      // Fire and forget
      Promise.all([
        this.createSession(userUID, sessionId, forceLogout),
        this.logEnhancedLogin(userDocId, userUID)
      ]).catch(error => {
        console.error('Background session error:', error);
      });
    },

    getOrCreateSessionId() {
      let sessionId = localStorage.getItem('nexus_session_id');
      if (!sessionId) {
        sessionId = 'session_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
        localStorage.setItem('nexus_session_id', sessionId);
      }
      return sessionId;
    },

    async createSession(userUID, sessionId, forceLogout = false) {
      try {
        // Deactivate existing sessions
        const existingSessionQuery = query(
          collection(db, "activeSessions"), 
          where("userUID", "==", userUID),
          where("isActive", "==", true)
        );
        const existingSessions = await getDocs(existingSessionQuery);
        
        const deactivatePromises = existingSessions.docs.map(sessionDoc =>
          updateDoc(doc(db, "activeSessions", sessionDoc.id), {
            isActive: false,
            endTime: serverTimestamp(),
            endReason: forceLogout ? 'forced_logout' : 'new_login'
          })
        );
        
        await Promise.all(deactivatePromises);

        // Create new active session
        await addDoc(collection(db, "activeSessions"), {
          userUID: userUID,
          sessionId: sessionId,
          isActive: true,
          startTime: serverTimestamp(),
          userAgent: navigator.userAgent,
          ipAddress: this.cachedIP || 'Unknown',
          deviceInfo: this.getDeviceInfo()
        });

        console.log('Session created successfully');
      } catch (error) {
        console.error("Error creating session:", error);
      }
    },

    async logEnhancedLogin(userDocId, userUID) {
      try {
        const loginLogData = {
          userDocId: userDocId,
          userUID: userUID,
          timestamp: serverTimestamp(),
          sessionId: localStorage.getItem('nexus_session_id'),
          ipAddress: this.cachedIP || 'Unknown',
          deviceInfo: this.getDeviceInfo(),
          userAgent: navigator.userAgent,
          loginType: 'web_login'
        };

        await addDoc(collection(db, "loginLogs"), loginLogData);
        console.log("Login logged successfully");
      } catch (error) {
        console.error("Error logging login:", error);
      }
    },

    getDeviceInfo() {
      const userAgent = navigator.userAgent;
      let deviceType = 'Desktop';
      
      if (/Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
        deviceType = 'Mobile';
      } else if (/iPad/i.test(userAgent)) {
        deviceType = 'Tablet';
      }
      
      return {
        type: deviceType,
        browser: this.getBrowserInfo(),
        os: this.getOSInfo()
      };
    },

    getBrowserInfo() {
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf("Chrome") > -1) return "Chrome";
      if (userAgent.indexOf("Firefox") > -1) return "Firefox";
      if (userAgent.indexOf("Safari") > -1) return "Safari";
      if (userAgent.indexOf("Edge") > -1) return "Edge";
      return "Unknown";
    },

    getOSInfo() {
      const userAgent = navigator.userAgent;
      if (userAgent.indexOf("Windows") > -1) return "Windows";
      if (userAgent.indexOf("Mac") > -1) return "macOS";
      if (userAgent.indexOf("Linux") > -1) return "Linux";
      if (userAgent.indexOf("Android") > -1) return "Android";
      if (userAgent.indexOf("iOS") > -1) return "iOS";
      return "Unknown";
    },

    showAlert(message, type = 'info') {
      this.alert = {
        visible: true,
        message: message,
        type: type
      };
    },

    togglePasswordVisibility() {
      this.passwordVisible = !this.passwordVisible;
    },

    showForgotPasswordDialog() {
      this.forgotPasswordDialog = true;
    },

    async sendPasswordResetRequest() {
      if (!this.forgotPasswordEmail) {
        this.resetAlert = { 
          visible: true, 
          message: 'Please enter your email', 
          type: 'error' 
        };
        return;
      }

      try {
        await addDoc(collection(db, 'passwordResetRequests'), {
          userEmail: this.forgotPasswordEmail,
          requestedAt: serverTimestamp(),
          status: 'pending',
          handledBy: null,
          newPasswordSet: false
        });
        
        this.resetAlert = { 
          visible: true, 
          message: 'Password reset request sent to administrator. They will contact you shortly.', 
          type: 'success' 
        };
        
        setTimeout(() => {
          this.forgotPasswordDialog = false;
          this.forgotPasswordEmail = '';
          this.resetAlert.visible = false;
        }, 3000);
        
      } catch (error) {
        console.error('Error submitting password reset request:', error);
        this.resetAlert = { 
          visible: true, 
          message: `Error: ${error.message}`, 
          type: 'error' 
        };
      }
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@400;700&display=swap');

.full-page {
  position: relative;
  height: 100vh;
  display: flex;
  justify-content: center;
  align-items: center;
  background-size: cover;
  background-repeat: no-repeat;
  background-position: center;
}

.full-page::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 1;
}

.login-card {
  z-index: 2;
  background-color: rgba(255, 255, 255, 0.9);
  border-radius: 20px;
  overflow: hidden;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
  width: 100%;
  transition: all 0.3s ease-in-out;
}

@media (min-width: 960px) {
  .login-card {
    margin-left: 100%;
  }
}

.card-header {
  background-color: #f0f0f0;
  border-bottom: 1px solid #ddd;
  padding: 20px;
  text-align: center;
}

.text-center {
  text-align: center;
}

.logo-image {
  width: 200px;
  height: auto;
  margin-bottom: 10px;
}

.login-text {
  color: #991d23;
  font-size: 1.6rem;
  animation: fadeIn 1s ease-in-out;
}

.additional-links {
  display: flex;
  justify-content: space-between;
  margin-top: 20px;
  animation: fadeIn 1.2s ease-in-out;
}

.montserrat-title {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  animation: fadeIn 1s ease-in-out;
}

.montserrat-input {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  animation: fadeIn 1.2s ease-in-out;
  padding: 10px 15px;
}

.montserrat-button {
  font-family: "Montserrat", sans-serif;
  font-weight: 700;
  animation: fadeIn 1.4s ease-in-out;
}

.montserrat-alert {
  font-family: "Montserrat", sans-serif;
  font-weight: 400;
  animation: fadeIn 0.8s ease-in-out;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.fade-in {
  animation: fadeIn 1s ease-in-out;
}

.scale-transition {
  transition: transform 0.3s ease-in-out;
  transform: scale(0.95);
}

@media (max-width: 600px) {
  .montserrat-input {
    padding: 8px 12px;
  }
}

.my-4 {
  margin-top: 16px;
  margin-bottom: 16px;
}

.scale-transition-enter-active,
.scale-transition-leave-active {
  transition: transform 0.3s ease-in-out;
}

.scale-transition-enter, 
.scale-transition-leave-to {
  transform: scale(1);
}
</style>